import api from "./api";

/**
 * Web Push helper.
 *
 * Responsibilities:
 *   - Detect platform support + iOS PWA requirement
 *   - Subscribe / unsubscribe the user's current device against the server
 *   - Send the PushSubscription up to /api/push/subscribe so the server
 *     can target this device via VAPID
 *
 * Notes on iOS:
 *   - iOS 16.4+ supports Web Push BUT only when the PWA has been
 *     "Added to Home Screen". Detect via display-mode: standalone.
 */

const VAPID_PUBLIC_KEY =
  process.env.REACT_APP_VAPID_PUBLIC_KEY ||
  ""; // public; non-secret

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isIOS() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/i.test(ua) && !window.MSStream;
}

export function isPWA() {
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    // Safari iOS standalone flag
    window.navigator.standalone === true
  );
}

/** Map base64url public key → Uint8Array for PushManager.subscribe */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  // `serviceWorker.ready` never rejects — if no SW is registered (CRA dev
  // build, headless test, or a user who hit the page before the SW
  // installed) it hangs forever. Try `getRegistration()` first (resolves
  // immediately with `undefined` when none) and race `.ready` with a 2 s
  // timeout to keep the UI snappy.
  try {
    const existing = await navigator.serviceWorker.getRegistration();
    if (existing) return existing;
  } catch {
    // ignore — fall through to .ready race
  }
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((resolve) => setTimeout(() => resolve(null), 2000)),
    ]);
  } catch {
    return null;
  }
}

export async function getPermission() {
  if (typeof Notification === "undefined") return "denied";
  return Notification.permission;
}

export async function requestPermission() {
  if (typeof Notification === "undefined") return "denied";
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

/** Returns the existing PushSubscription for this browser, or null. */
export async function currentSubscription() {
  if (!pushSupported()) return null;
  const reg = await getRegistration();
  if (!reg) return null;
  return await reg.pushManager.getSubscription();
}

/** Fetches the server's VAPID public key (also tells us if push is configured). */
export async function fetchServerStatus() {
  try {
    const { data } = await api.get("/push/key");
    return data;
  } catch {
    return { configured: false, public_key: null };
  }
}

/** Subscribe THIS device. Returns the saved server record. */
export async function subscribeThisDevice() {
  if (!pushSupported()) {
    throw new Error("Push notifications aren't supported on this browser.");
  }
  if (isIOS() && !isPWA()) {
    throw new Error(
      "On iOS, install FlowTasks360 to your Home Screen first to enable push (Safari ▸ Share ▸ Add to Home Screen)."
    );
  }
  const perm = await requestPermission();
  if (perm !== "granted") {
    throw new Error("Notifications permission was not granted.");
  }
  const reg = await getRegistration();
  if (!reg) throw new Error("Service worker not registered yet — please reload.");
  let serverKey = VAPID_PUBLIC_KEY;
  if (!serverKey) {
    const status = await fetchServerStatus();
    serverKey = status.public_key || "";
  }
  if (!serverKey) {
    throw new Error("Push is not configured on the server (missing VAPID key).");
  }
  // Already subscribed? Reuse.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(serverKey),
    });
  }
  // Compute a friendly device label
  const label =
    (isIOS() && (isPWA() ? "iPhone (Home Screen)" : "iPhone Safari")) ||
    (/Android/i.test(navigator.userAgent) && "Android") ||
    (/Mac/i.test(navigator.userAgent) && "Mac") ||
    (/Win/i.test(navigator.userAgent) && "Windows") ||
    "This device";

  const subJson = sub.toJSON();
  const { data } = await api.post("/push/subscribe", {
    endpoint: subJson.endpoint,
    keys: subJson.keys,
    device_label: label,
    user_agent: navigator.userAgent,
    locale: navigator.language,
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone || null,
  });
  return data;
}

/** Unsubscribe THIS device. */
export async function unsubscribeThisDevice() {
  const sub = await currentSubscription();
  if (!sub) return { deleted: 0 };
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch {}
  try {
    const { data } = await api.post("/push/unsubscribe", { endpoint });
    return data;
  } catch {
    return { deleted: 0 };
  }
}

/** Server-side device list. */
export async function listDevices() {
  const { data } = await api.get("/push/devices");
  return data.devices || [];
}

export async function removeDevice(id) {
  await api.delete(`/push/devices/${id}`);
}

/** Trigger a server-side test push to every device the user has registered. */
export async function sendTest() {
  const { data } = await api.post("/push/test", {});
  return data;
}
